/**
 * ConditionsDataTable - Conditions table using the reusable DataTable component
 * Migrated from inline MUI Table in Conditions.tsx to v2 DataTable
 */

// react
import React, { ReactNode, useCallback, useContext, useMemo } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { alpha, Avatar, Box, Chip, Stack, Typography } from '@mui/material';

// icons
import {
	Assignment as IconWorkPlan,
	Description as IconContract,
	Visibility as IconView,
	Edit as IconEdit,
	Link as IconLink,
	Archive as IconArchive,
} from '@mui/icons-material';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// components
import { ColumnDef, DataTable, FilterConfig, RowAction, SortDirection } from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// types
import { Condition } from '../../../../@types';

// data
import { COLORS, GRADIENTS } from '../../../../data';

// utils
import { getContractNum } from '../Contracts/Contracts.utils';

// Types

/** Flattened condition row with resolved references for display */
export interface ConditionRow extends Condition {
	level: string;
	levelName: string;
	workPlanName?: string;
	activityName?: string;
	contractName?: string;
}

interface ConditionsDataTableProps {
	/** Flattened and filtered conditions for display */
	data: ConditionRow[];
	/** Whether data is loading */
	isLoading?: boolean;
	/** Current search value */
	searchValue?: string;
	/** Search change handler */
	onSearchChange?: (value: string) => void;
	/** Current sort column */
	orderBy?: string;
	/** Current sort direction */
	order?: SortDirection;
	/** Sort change handler */
	onSortChange?: (orderBy: string, order: SortDirection) => void;
	/** Current page (1-indexed) */
	page?: number;
	/** Rows per page */
	rowsPerPage?: number;
	/** Total count for pagination */
	totalCount?: number;
	/** Page change handler */
	onPageChange?: (page: number) => void;
	/** Rows per page change handler */
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	/** Filter values */
	filterValues?: Record<string, string | number>;
	/** Filter change handler */
	onFilterChange?: (key: string, value: string | number) => void;
	/** Row click handler */
	onRowClick?: (condition: ConditionRow) => void;
	/** Edit condition handler */
	onEditCondition?: (condition: ConditionRow) => void;
	/** Link to procurement handler */
	onLinkProcurement?: (condition: ConditionRow) => void;
	/** Archive condition handler */
	onArchiveCondition?: (condition: ConditionRow) => void;
	/** Empty state: create with AI handler */
	onCreateWithAI?: () => void;
	/** Optional footer content (e.g., quick-add row) */
	footer?: ReactNode;
}

// Helpers

/** Get icon for condition type (Payment Condition vs Base Condition) */
const getConditionLevelIcon = (level: string) => {
	switch (level) {
		case 'Payment Condition':
			return <IconContract sx={{ color: GRADIENTS.contract.icon, fontSize: 18 }} />;
		case 'Base Condition':
		default:
			return <IconWorkPlan sx={{ color: GRADIENTS.workPlan.icon, fontSize: 18 }} />;
	}
};

/** Get status chip styling */
const getStatusChipStyle = (status: string, t: (key: string) => string) => {
	switch (status) {
		case 'VERIFIED':
		case 'APPROVED':
			return {
				bgcolor: alpha(COLORS.success.main, 0.1),
				color: COLORS.success.main,
				label: t('approved'),
			};
		case 'PENDING':
		case 'SUBMITTED':
			return {
				bgcolor: alpha(COLORS.warning.main, 0.1),
				color: COLORS.warning.main,
				label: t('pending'),
			};
		case 'REJECTED':
			return {
				bgcolor: alpha(COLORS.error.main, 0.1),
				color: COLORS.error.main,
				label: t('rejected'),
			};
		default:
			return { bgcolor: 'grey.100', color: 'text.secondary', label: status };
	}
};

/** Format currency with BDT abbreviation */
const formatAmount = (amount: number) => {
	if (amount >= 1000000000) {
		return `BDT ${(amount / 1000000000).toFixed(1)}B`;
	} else if (amount >= 1000000) {
		return `BDT ${(amount / 1000000).toFixed(0)}M`;
	} else if (amount >= 1000) {
		return `BDT ${(amount / 1000).toFixed(0)}K`;
	}
	return `BDT ${amount.toLocaleString()}`;
};

// Component

export const ConditionsDataTable = ({
	data,
	isLoading = false,
	searchValue = '',
	onSearchChange,
	orderBy,
	order = 'ASC',
	onSortChange,
	page = 1,
	rowsPerPage = 20,
	totalCount,
	onPageChange,
	onRowsPerPageChange,
	filterValues = {},
	onFilterChange,
	onRowClick,
	onEditCondition,
	onLinkProcurement,
	onArchiveCondition,
	onCreateWithAI,
	footer,
}: ConditionsDataTableProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { project } = useContext(ContextProject);
	const { workPlans, contracts } = useContext(ContextSpendPlan);

	// Navigation
	const handleOpenCondition = useCallback(
		(conditionId: number) => {
			navigate(`/project/${project?.idWbg}/spend-plans/condition/${conditionId}`);
		},
		[navigate, project?.idWbg]
	);

	// Column definitions
	const columns: ColumnDef<ConditionRow>[] = useMemo(
		() => [
			{
				id: 'type',
				label: t('type'),
				minWidth: 180,
				sortable: true,
				sortKey: 'conditionType',
				render: (condition) => (
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<Avatar
							sx={{
								bgcolor:
									condition.level === t('spendPlan.conditions.paymentCondition')
										? alpha(GRADIENTS.contract.icon, 0.12)
										: alpha(GRADIENTS.workPlan.icon, 0.12),
								color:
									condition.level === t('spendPlan.conditions.paymentCondition')
										? GRADIENTS.contract.icon
										: GRADIENTS.workPlan.icon,
								height: 28,
								width: 28,
							}}>
							{getConditionLevelIcon(condition.level)}
						</Avatar>
						<Box sx={{ minWidth: 0 }}>
							<TruncatedText
								text={condition.levelName}
								variant="body2"
								sx={{ fontWeight: 500, maxWidth: 140 }}
							/>
							<Typography variant="caption" color="text.secondary">
								{condition.level}
							</Typography>
						</Box>
					</Stack>
				),
			},
			{
				id: 'name',
				label: t('spendPlan.conditions.conditionName'),
				minWidth: 240,
				sortable: true,
				render: (condition) => (
					<Box sx={{ minWidth: 0, maxWidth: 300 }}>
						<TruncatedText
							text={condition.name}
							variant="body2"
							sx={{ fontWeight: 500 }}
						/>
						{condition.description && (
							<TruncatedText
								text={condition.description}
								variant="caption"
								color="text.secondary"
								sx={{ display: 'block' }}
							/>
						)}
					</Box>
				),
			},
			{
				id: 'allocated',
				label: t('allocated'),
				minWidth: 100,
				align: 'right',
				sortable: true,
				sortKey: 'paymentAmount',
				render: (condition) => (
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{formatAmount(condition.paymentAmount || 0)}
					</Typography>
				),
			},
			{
				id: 'paymentPercentage',
				label: t('spendPlan.conditions.paymentPct'),
				minWidth: 90,
				align: 'right',
				sortable: true,
				render: (condition) => (
					<Typography variant="body2" sx={{ fontWeight: 600 }}>
						{condition.paymentPercentage || 0}%
					</Typography>
				),
			},
			{
				id: 'documents',
				label: t('documents'),
				minWidth: 90,
				align: 'center',
				sortable: false,
				render: (condition) => (
					<Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
						<Typography variant="body2" color="text.secondary">
							{(condition.dataRequestFields || []).filter((f) => f.fieldType === 'document').length}
						</Typography>
						<IconView sx={{ color: 'text.secondary', fontSize: 18 }} />
					</Stack>
				),
			},
			{
				id: 'status',
				label: t('status'),
				minWidth: 90,
				sortable: true,
				render: (condition) => {
					const statusStyle = getStatusChipStyle(condition.status, t);
					return (
						<Chip
							label={statusStyle.label}
							size="small"
							sx={{
								bgcolor: statusStyle.bgcolor,
								color: statusStyle.color,
								fontWeight: 500,
								fontSize: '0.75rem',
							}}
						/>
					);
				},
			},
		],
		[]
	);

	// Row actions
	const rowActions: RowAction<ConditionRow>[] = useMemo(
		() => [
			{
				label: 'Edit',
				icon: <IconEdit sx={{ fontSize: 18, color: '#6b7280' }} />,
				onClick: (condition) => onEditCondition?.(condition),
			},
			{
				label: 'Link to Procurement',
				icon: <IconLink sx={{ fontSize: 18, color: '#6b7280' }} />,
				onClick: (condition) => onLinkProcurement?.(condition),
			},
			{
				label: 'Archive',
				icon: <IconArchive sx={{ fontSize: 18, color: '#6b7280' }} />,
				onClick: (condition) => onArchiveCondition?.(condition),
				dividerBefore: true,
			},
		],
		[onEditCondition, onLinkProcurement, onArchiveCondition]
	);

	// Filter configurations
	const filterConfigs: FilterConfig[] = useMemo(
		() => [
			{
				key: 'component',
				label: 'Component',
				options: [
					{ value: 'ALL', label: 'All Components' },
					...workPlans.map((wp) => ({ value: String(wp.id), label: wp.name })),
				],
			},
			{
				key: 'contract',
				label: 'Contract',
				options: [
					{ value: 'ALL', label: 'All Contracts' },
					...contracts.map((c) => ({
						value: String(c.id),
						label: getContractNum(c) || `Contract ${c.id}`,
					})),
				],
			},
			{
				key: 'status',
				label: t('status'),
				options: [
					{ value: 'ALL', label: 'All Statuses' },
					{ value: 'APPROVED', label: 'Approved' },
					{ value: 'PENDING', label: t('pending') },
					{ value: 'REJECTED', label: 'Rejected' },
					{ value: 'SUBMITTED', label: 'Submitted' },
					{ value: 'VERIFIED', label: 'Verified' },
				],
			},
		],
		[workPlans, contracts]
	);

	return (
		<DataTable
			data={data}
			columns={columns}
			getRowKey={(condition) => condition.id}
			loading={isLoading}
			emptyMessage={searchValue ? t('empty.default.title') : t('spendPlan.empty.conditions.title')}
			emptyContent={
				searchValue ? t('spendPlan.tryAdjustingFilters') : t('spendPlan.empty.conditions.content')
			}
			emptyActions={
				onCreateWithAI
					? [
							{
								children: t('spendPlan.createWithAI'),
								onClick: onCreateWithAI,
								variant: 'contained',
							},
						]
					: undefined
			}
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
			searchPlaceholder={t('spendPlan.conditions.searchPlaceholder')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
			showFilters
			filters={filterConfigs}
			filterValues={filterValues}
			onFilterChange={onFilterChange}
			rowActions={rowActions}
			onRowClick={(condition) =>
				onRowClick ? onRowClick(condition) : handleOpenCondition(condition.id)
			}
			footer={footer}
		/>
	);
};

export default ConditionsDataTable;
